import {useEffect, useId, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode} from 'react';

interface CvSectionProps {
    children: ReactNode;
    className?: string;
    /** Placeholder height while the section is skipped. Default: 1200px. */
    intrinsicSize?: string;
    id?: string;
}

// How far outside the viewport a below-the-fold section is brought to life: far enough that it happens off screen,
// and before its FadeInUp observers would need to fire.
const ACTIVATE_MARGIN_PX = 800;

const subscribeNoop = () => () => undefined;

/** True only while React hydrates the server HTML (it reads the server snapshot then), false on client renders. */
const useIsHydrating = () =>
    useSyncExternalStore(
        subscribeNoop,
        () => false,
        () => true
    );

/** Child-index path from `root` down to `node`, to find the same element again after the section re-renders. */
const pathTo = (root: Element, node: Element): number[] => {
    const path: number[] = [];
    for (let n: Element | null = node; n && n !== root; n = n.parentElement) {
        if (!n.parentElement) return [];
        path.unshift(Array.prototype.indexOf.call(n.parentElement.children, n));
    }
    return path;
};

const followPath = (root: Element, path: number[]): Element | null =>
    path.reduce<Element | null>((n, i) => (n ? n.children[i] ?? null : null), root);

const isNearViewport = (el: Element) => {
    const rect = el.getBoundingClientRect();
    return rect.top < globalThis.innerHeight + ACTIVATE_MARGIN_PX && rect.bottom > -ACTIVATE_MARGIN_PX;
};

/**
 * Plain stand-in for Common/CvSection: `content-visibility: auto` (markup stays in the HTML), plus lazy hydration.
 *
 * A section that is far below the fold when the page hydrates keeps its server HTML as-is (React leaves a
 * `dangerouslySetInnerHTML` node's existing content alone while hydrating) and none of its components run. When it
 * comes within ACTIVATE_MARGIN_PX of the viewport it renders normally — the same markup, rebuilt off screen.
 * Sections on or near the screen at load hydrate as usual, and client-side renders (navigation) are never deferred.
 */
const CvSection = ({children, className, intrinsicSize = '1200px', id}: CvSectionProps) => {
    const cvKey = useId();
    const hydrating = useIsHydrating();
    const [deferred, setDeferred] = useState(() => {
        if (!hydrating || typeof document === 'undefined') return false;
        const el = document.querySelector(`[data-cv="${CSS.escape(cvKey)}"]`);
        return !!el && !isNearViewport(el);
    });

    // A click that lands before the section is live (e.g. right after jumping to it) is replayed once it is.
    const pendingClick = useRef<number[] | null>(null);

    useEffect(() => {
        if (!deferred) return undefined;
        const el = document.querySelector(`[data-cv="${CSS.escape(cvKey)}"]`);
        if (!el || typeof IntersectionObserver === 'undefined') {
            setDeferred(false);
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) setDeferred(false);
            },
            {rootMargin: `${ACTIVATE_MARGIN_PX}px 0px`}
        );
        observer.observe(el);
        const onEarlyClick = (event: Event) => {
            const target = event.target as Element;
            // Plain links already work without React; let the browser follow them.
            if (target.closest('a[href]')) return;
            event.preventDefault();
            pendingClick.current = pathTo(el, target);
            setDeferred(false);
        };
        el.addEventListener('click', onEarlyClick, true);
        return () => {
            observer.disconnect();
            el.removeEventListener('click', onEarlyClick, true);
        };
    }, [deferred, cvKey]);

    useEffect(() => {
        if (deferred || !pendingClick.current) return;
        const path = pendingClick.current;
        pendingClick.current = null;
        const el = document.querySelector(`[data-cv="${CSS.escape(cvKey)}"]`);
        const target = el && followPath(el, path);
        if (target instanceof HTMLElement) target.click();
    }, [deferred, cvKey]);

    const style: CSSProperties = {contentVisibility: 'auto', containIntrinsicSize: `auto ${intrinsicSize}`};

    if (deferred) {
        return (
            <div
                key="deferred"
                className={className}
                style={style}
                id={id}
                data-cv={cvKey}
                // Keeps the server-rendered children in place, untouched, until the section nears the viewport.
                dangerouslySetInnerHTML={{__html: ''}}
                suppressHydrationWarning
            />
        );
    }
    // A different key, so activating replaces the whole element: React never reuses (and so never duplicates)
    // the server children it was told to leave alone.
    return (
        <div key="live" className={className} style={style} id={id} data-cv={cvKey}>
            {children}
        </div>
    );
};

export default CvSection;
